import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    createProject,
    BrandGuideCreate,
    generateBrandGuide,
    updateBrandGuide,
    setDefaultBrandGuide,
} from '../../services/api';
import { GlassCard } from '../ui/GlassCard';
import { Loader2 } from 'lucide-react';
import './ProjectWizard.css';

// --- Step Components ---

// Step 1: Project Details (Ported from NewProject.tsx)
interface Step1Props {
    projectData: { name: string; description: string; masterplan: File | null };
    onChange: (field: string, value: string | File | null) => void;
}

const Step1ProjectDetails: React.FC<Step1Props> = ({ projectData, onChange }) => {
    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            onChange('masterplan', e.target.files[0]);
        }
    }, [onChange]);
    
    return (
        <div className="wizard-step">
            <h3>Step 1: Project & Context</h3>
            <div className="form-group">
                <label htmlFor="name">Project Name <span className="required">*</span></label>
                <input
                    id="name"
                    type="text"
                    value={projectData.name}
                    onChange={(e) => onChange('name', e.target.value)}
                    required
                />
            </div>
            <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                    id="description"
                    rows={4}
                    value={projectData.description}
                    onChange={(e) => onChange('description', e.target.value)}
                />
            </div>
            <div className="form-group">
                <label htmlFor="masterplan">SEO Masterplan File (Optional)</label>
                <input
                    id="masterplan"
                    type="file"
                    accept=".html,.txt,.md"
                    onChange={handleFileChange}
                />
            </div>
        </div>
    );
};

// Step 2: Brand Fundamentals
interface Step2Props {
    brandData: Partial<BrandGuideCreate>;
    onChange: (field: keyof BrandGuideCreate, value: string) => void;
}

const Step2BrandFundamentals: React.FC<Step2Props> = ({ brandData, onChange }) => (
    <div className="wizard-step">
        <h3>Step 2: Brand Fundamentals</h3>
        <p>Define the core identity of your brand.</p>
        <div className="form-group">
            <label htmlFor="brandName">Brand Name <span className="required">*</span></label>
            <input
                id="brandName"
                type="text"
                value={brandData.brand_name || ''}
                onChange={(e) => onChange('brand_name', e.target.value)}
                required
            />
        </div>
        <div className="form-group">
            <label htmlFor="tagline">Tagline</label>
            <input
                id="tagline"
                type="text"
                value={brandData.tagline || ''}
                onChange={(e) => onChange('tagline', e.target.value)}
            />
        </div>
        <div className="form-group">
            <label htmlFor="mission">Mission Statement</label>
            <textarea
                id="mission"
                rows={4}
                value={brandData.mission_statement || ''}
                onChange={(e) => onChange('mission_statement', e.target.value)}
            />
        </div>
        <div className="form-group">
            <label htmlFor="voice">Brand Voice</label>
            <textarea
                id="voice"
                rows={4}
                value={brandData.brand_voice || ''}
                onChange={(e) => onChange('brand_voice', e.target.value)}
            />
        </div>
    </div>
);


// Step 3: Visual Assets
const Step3VisualAssets: React.FC<Step2Props> = ({ brandData, onChange }) => (
    <div className="wizard-step">
        <h3>Step 3: Visual Assets (Core)</h3>
        <p>Establish primary colors and fonts.</p>
        <div className="form-group-grid">
            <div className="form-group">
                <label htmlFor="primaryColor">Primary Color (HEX) <span className="required">*</span></label>
                <input
                    id="primaryColor"
                    type="color"
                    value={brandData.primary_color || '#3498db'}
                    onChange={(e) => onChange('primary_color', e.target.value)}
                />
                <input
                    type="text"
                    value={brandData.primary_color || '#3498db'}
                    onChange={(e) => onChange('primary_color', e.target.value)}
                    required
                    pattern="#[a-fA-F0-9]{6}"
                    title="Must be a 6-digit hex code"
                />
            </div>
            <div className="form-group">
                <label htmlFor="secondaryColor">Secondary Color (HEX)</label>
                <input
                    id="secondaryColor"
                    type="color"
                    value={brandData.secondary_color || '#2c3e50'}
                    onChange={(e) => onChange('secondary_color', e.target.value)}
                />
                <input
                    type="text"
                    value={brandData.secondary_color || '#2c3e50'}
                    onChange={(e) => onChange('secondary_color', e.target.value)}
                    pattern="#[a-fA-F0-9]{6}"
                />
            </div>
        </div>
        <div className="form-group-grid">
            <div className="form-group">
                <label htmlFor="primaryFont">Primary Font</label>
                <input
                    id="primaryFont"
                    type="text"
                    value={brandData.primary_font || 'Inter'}
                    onChange={(e) => onChange('primary_font', e.target.value)}
                />
            </div>
            <div className="form-group">
                <label htmlFor="secondaryFont">Secondary Font</label>
                <input
                    id="secondaryFont"
                    type="text"
                    value={brandData.secondary_font || 'Inter'}
                    onChange={(e) => onChange('secondary_font', e.target.value)}
                />
            </div>
        </div>
        <p className="note">Logo upload is handled separately in the Brand Portal after creation.</p>
    </div>
);

// Step 4: Finalize & Generate
interface Step4Props {
    brandData: Partial<BrandGuideCreate>;
    projectData: { name: string; description: string; masterplan: File | null };
}
const Step4Finalize: React.FC<Step4Props> = ({ brandData, projectData }) => (
    <div className="wizard-step">
        <h3>Step 4: Review & Finalize</h3>
        <p>Review the details before creating your project and brand guide.</p>
        
        <div className="review-block">
            <h4>Project Summary</h4>
            <p><strong>Name:</strong> {projectData.name}</p>
            <p><strong>Description:</strong> {projectData.description || 'N/A'}</p>
            <p><strong>Masterplan:</strong> {projectData.masterplan ? projectData.masterplan.name : 'N/A'}</p>
        </div>

        <div className="review-block">
            <h4>Brand Summary</h4>
            <p><strong>Brand Name:</strong> {brandData.brand_name}</p>
            <p><strong>Mission:</strong> {brandData.mission_statement ? `${brandData.mission_statement.substring(0, 50)}...` : 'N/A'}</p>
            <p><strong>Primary Color:</strong> <span style={{ color: brandData.primary_color }}>{brandData.primary_color}</span></p>
        </div>
        
        <p className="note">Click 'Create Project' below to finalize the setup and generate your initial tasks.</p>
    </div>
);


// --- Main Wizard Component ---

const ProjectWizard = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const [projectData, setProjectData] = useState({ name: '', description: '', masterplan: null as File | null });
    const [brandData, setBrandData] = useState<Partial<BrandGuideCreate>>({ 
        brand_name: '',
        primary_color: '#3498db',
    });
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    // Status message shown alongside the spinner so the user knows the wait
    // (the brand-guide generation step calls Gemini and takes 10-15s).
    const [statusMessage, setStatusMessage] = useState('Creating your project…');
    const navigate = useNavigate();

    const steps = [
        { id: 1, name: 'Project Info', component: Step1ProjectDetails, required: ['name'] },
        { id: 2, name: 'Brand Identity', component: Step2BrandFundamentals, required: ['brand_name', 'primary_color'] },
        { id: 3, name: 'Visual Assets', component: Step3VisualAssets, required: [] },
        { id: 4, name: 'Finalize', component: Step4Finalize, required: [] },
    ];

    const CurrentStepComponent = steps[currentStep - 1].component;
    const requiredFields = steps[currentStep - 1].required;

    const handleProjectDataChange = useCallback((field: string, value: string | File | null) => {
        setProjectData(prev => ({ ...prev, [field]: value as any }));
    }, []);

    const handleBrandDataChange = useCallback((field: keyof BrandGuideCreate, value: string) => {
        setBrandData(prev => ({ ...prev, [field]: value }));
    }, []);

    const validateStep = () => {
        for (const field of requiredFields) {
            // Check project data
            if (field in projectData && !(projectData as any)[field]) {
                setError(`${field} is required.`);
                return false;
            }
            // Check brand data
            if (field in brandData && !(brandData as any)[field]) {
                setError(`Brand ${field} is required.`);
                return false;
            }
        }
        setError(null);
        return true;
    };
    
    const handleNext = () => {
        if (validateStep()) {
            setCurrentStep(prev => Math.min(prev + 1, steps.length));
        }
    };
    
    const handlePrev = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateStep()) return;
        
        setLoading(true);
        setError(null);

        let masterplanContent = '';
        if (projectData.masterplan) {
            try {
                masterplanContent = await projectData.masterplan.text();
            } catch (err) {
                setError('Failed to read the masterplan file.');
                setLoading(false);
                return;
            }
        }
        
        try {
            // 1. Create the project
            setStatusMessage('Creating your project…');
            const project = await createProject({
                name: projectData.name,
                description: projectData.description,
                masterplan_document: masterplanContent,
            });

            // 2. Build a brand guide via the AI generator (same code path as
            //    the Brand Portal's 'Generate' button) so the result is a
            //    full guide with brand_story / mission / tone_attributes /
            //    messaging_pillars / example_headlines / example_ctas /
            //    design_style / writing_guidelines populated — not a stub
            //    with only the wizard's basic fields. The wizard's user
            //    inputs are passed as a custom_prompt so the AI uses them
            //    as authoritative seeds.
            setStatusMessage('Generating your brand guide (~30s)…');
            const brandName = brandData.brand_name || project.name;
            const customPrompt = `Generate a comprehensive brand guide for "${brandName}".

Project description: ${projectData.description || '(not provided)'}
${brandData.tagline ? `Tagline: ${brandData.tagline}` : ''}
${brandData.mission_statement ? `Mission statement: ${brandData.mission_statement}` : ''}
${brandData.brand_voice ? `Brand voice: ${brandData.brand_voice}` : ''}

CRITICAL — preserve these user-specified values exactly. Do NOT invent alternatives:
- Brand name: ${brandName}
- Primary color: ${brandData.primary_color || '#3498DB'}
${brandData.secondary_color ? `- Secondary color: ${brandData.secondary_color}` : ''}
${brandData.primary_font ? `- Primary font: ${brandData.primary_font}` : ''}
${brandData.secondary_font ? `- Secondary font: ${brandData.secondary_font}` : ''}

Use the project description, mission statement, and brand voice to inform brand_story, tone_attributes (4-6 short adjectives), messaging_pillars (3 short statements), example_headlines (3-5 short headlines), example_ctas (3-5 button labels), design_style (one sentence), writing_guidelines (one paragraph). Keep all output appropriate to the brand voice and tone.`;

            const generated = await generateBrandGuide({
                project_id: project.project_id,
                brand_guide_name: `${project.name} Guide`,
                source_type: 'manual_prompt',
                custom_prompt: customPrompt,
            });

            // 3. PATCH to enforce user's explicit choices (the AI may have
            //    drifted from the prompt) + carry through fields the user
            //    typed but the AI may not have honoured. Then mark default.
            setStatusMessage('Finalising…');
            const enforced: Partial<BrandGuideCreate> = {
                brand_name: brandName,
                primary_color: brandData.primary_color || '#3498DB',
            };
            if (brandData.secondary_color) enforced.secondary_color = brandData.secondary_color;
            if (brandData.primary_font) enforced.primary_font = brandData.primary_font;
            if (brandData.secondary_font) enforced.secondary_font = brandData.secondary_font;
            if (brandData.tagline) enforced.tagline = brandData.tagline;
            if (brandData.mission_statement) enforced.mission_statement = brandData.mission_statement;
            if (brandData.brand_voice) enforced.brand_voice = brandData.brand_voice;
            await updateBrandGuide(generated.brand_guide_id, enforced);
            await setDefaultBrandGuide(generated.brand_guide_id);

            // Send the user to the Brand module so they can immediately see
            // and edit the freshly-generated brand guide. Layout's location-
            // state handler picks up selectProjectId so the sidebar's active
            // project switches to the new one too.
            navigate('/brand', {
                state: { refreshProjects: true, selectProjectId: project.project_id },
            });

        } catch (err) {
            setError('Failed to create project and brand guide.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    return (
        <div className="project-wizard-container">
            <h2>New Project Setup</h2>

            {/* Kanban Flow Visual Reference — glass card matching LandingPageCreator pattern */}
            <GlassCard variant="default" padding="md" className="kanban-flow-card">
                <div className="kanban-flow">
                    {steps.map(step => (
                        <div
                            key={step.id}
                            className={`kanban-step ${step.id === currentStep ? 'active' : ''} ${step.id < currentStep ? 'completed' : ''}`}
                            onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                        >
                            <span className="step-number">{step.id}</span>
                            <span className="step-name">{step.name}</span>
                        </div>
                    ))}
                </div>
            </GlassCard>

            <GlassCard variant="default" padding="lg" className="wizard-form-card">
                <form onSubmit={handleSubmit}>
                    {currentStep === 1 && (
                        <Step1ProjectDetails 
                            projectData={projectData} 
                            onChange={handleProjectDataChange}
                        />
                    )}
                    {currentStep === 2 && (
                        <Step2BrandFundamentals 
                            brandData={brandData}
                            onChange={handleBrandDataChange}
                        />
                    )}
                    {currentStep === 3 && (
                        <Step3VisualAssets 
                            brandData={brandData}
                            onChange={handleBrandDataChange}
                        />
                    )}
                    {currentStep === 4 && (
                        <Step4Finalize 
                            projectData={projectData}
                            brandData={brandData}
                        />
                    )}

                    {error && <p className="error-message">{error}</p>}

                    <div className="wizard-controls">
                        {currentStep > 1 && (
                            <button type="button" onClick={handlePrev} className="btn-secondary">
                                ← Back
                            </button>
                        )}
                        
                        {currentStep < steps.length && (
                            <button type="button" onClick={handleNext} className="btn-primary" disabled={loading}>
                                Next Step →
                            </button>
                        )}
                        
                        {currentStep === steps.length && (
                            <button type="submit" className="btn-primary btn-submit" disabled={loading}>
                                {loading ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                        <Loader2 size={16} className="wizard-spin" />
                                        {statusMessage}
                                    </span>
                                ) : 'Create Project'}
                            </button>
                        )}
                    </div>
                </form>
            </GlassCard>
        </div>
    );
};

export default ProjectWizard;